/**
 * SearchService — coordinates search requests (text, photo, crop)
 * Cancels previous requests on new search, updates state and emits events
 */

import { ENDPOINTS } from '../api/endpoints.js';
import { EVENTS } from '../core/events.js';

export default class SearchService {
  /**
   * @param {import('../api/ApiClient.js').default} apiClient
   * @param {import('../core/StateManager.js').default} stateManager
   * @param {import('../core/EventBus.js').default} eventBus
   * @param {import('../core/Config.js').default} config
   * @param {import('./FilterService.js').default} filterService
   */
  constructor(apiClient, stateManager, eventBus, config, filterService) {
    this._apiClient = apiClient;
    this._stateManager = stateManager;
    this._eventBus = eventBus;
    this._config = config;
    this._filterService = filterService;

    this._currentController = null;
    this._lastSearchOptions = null;
  }

  /**
   * Search by text query
   * @param {string} query
   * @param {Object} [options] - Additional search context
   * @param {number|null} [options.category_id] - Category ID (from suggest)
   * @param {number|null} [options.vendor_id] - Vendor ID (from suggest)
   * @param {string|null} [options.type] - Suggest type (category, brand, etc.)
   * @param {Object} [flags] - Internal flags
   * @param {boolean} [flags.resetFilters=true] - Whether to reset filters before search
   */
  async searchByText(query, options, { resetFilters = true } = {}) {
    this._cancelPrevious();
    if (resetFilters) this._filterService.resetFilters();
    this._lastSearchOptions = options || null;

    const controller = this._apiClient.createAbortController();
    this._currentController = controller;

    this._stateManager.setState({ status: 'loading', query });

    try {
      const body = {
        query,
        limit: this._config.limit,
        ...this._filterService.getFilterParams(),
      };

      // Pass category_id / vendor_id when searching from a suggest
      if (options && options.category_id) {
        body.category_id = options.category_id;
      }
      if (options && options.vendor_id) {
        body.vendor_id = options.vendor_id;
      }

      const result = await this._apiClient.post(ENDPOINTS.SEARCH, body, {
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;

      this._handleSearchResult(result);
    } catch (err) {
      if (err.name === 'AbortError') return;
      this._handleSearchError(err);
    } finally {
      if (this._currentController === controller) {
        this._currentController = null;
      }
    }
  }

  /**
   * Search by uploaded photo
   * @param {File} file
   */
  async searchByPhoto(file) {
    this._cancelPrevious();
    this._filterService.resetFilters();

    const controller = this._apiClient.createAbortController();
    this._currentController = controller;

    this._stateManager.setState({ status: 'loading', query: '' });

    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('limit', String(this._config.limit));

      // Add filter params to FormData
      const filters = this._filterService.getFilterParams();
      for (const [key, value] of Object.entries(filters)) {
        if (value === null || value === undefined) continue;
        if (Array.isArray(value)) {
          for (const v of value) {
            formData.append(key, String(v));
          }
        } else {
          formData.append(key, String(value));
        }
      }

      const result = await this._apiClient.postFormData(ENDPOINTS.SEARCH_BY_PHOTO, formData, {
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;

      this._handleSearchResult(result);
    } catch (err) {
      if (err.name === 'AbortError') return;
      this._handleSearchError(err);
    } finally {
      if (this._currentController === controller) {
        this._currentController = null;
      }
    }
  }

  /**
   * Search by image crop (visual search)
   * @param {string} imageUrl
   * @param {number[]} bbox - [x1, y1, x2, y2] relative coords (0-1)
   * @param {number|null} genderId
   */
  async searchByCrop(imageUrl, bbox, genderId) {
    this._cancelPrevious();
    this._filterService.resetFilters();

    const controller = this._apiClient.createAbortController();
    this._currentController = controller;

    this._stateManager.setState({ status: 'loading', query: '' });

    try {
      const body = {
        image_url: imageUrl,
        bbox,
        limit: this._config.limit,
        ...this._filterService.getFilterParams(),
      };
      if (genderId) {
        body.source_gender_id = genderId;
      }

      const result = await this._apiClient.post(ENDPOINTS.SEARCH_BY_IMAGE_CROP, body, {
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;

      this._handleSearchResult(result);
    } catch (err) {
      if (err.name === 'AbortError') return;
      this._handleSearchError(err);
    } finally {
      if (this._currentController === controller) {
        this._currentController = null;
      }
    }
  }

  /**
   * Re-run current search with updated filters
   */
  async reSearch() {
    const state = this._stateManager.getState();
    if (state.query) {
      await this.searchByText(state.query, this._lastSearchOptions, { resetFilters: false });
    }
  }

  /** @private Cancel the previous in-flight request */
  _cancelPrevious() {
    if (this._currentController) {
      this._currentController.abort();
      this._currentController = null;
    }
  }

  /** @private Handle successful search response */
  _handleSearchResult(result) {
    const data = result.data || result;
    const products = data.products || [];
    const facets = data.facets || {};
    const totalCount = data.total_count || 0;
    const currency = data.currency || 'RUB';

    const status = products.length > 0 ? 'results' : 'empty';

    this._stateManager.setState({
      status,
      products,
      facets,
      totalCount,
      currency,
    });

    this._eventBus.emit(EVENTS.SEARCH_RESULTS, {
      products,
      facets,
      totalCount,
      currency,
    });
  }

  /** @private Handle search error */
  _handleSearchError(err) {
    console.error('[GarderoboSearch] Search error:', err);
    this._stateManager.setState({ status: 'error' });
    this._eventBus.emit(EVENTS.SEARCH_ERROR, { error: err });
  }

  destroy() {
    this._cancelPrevious();
  }
}
